import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Unread conversation counts per channel ({whatsapp, email, chat, total}),
 * shared by the Communication tabs, the sidebar and the notification bell.
 *
 * One poll for the whole app, running only while something is subscribed.
 * `refreshUnreadSummary()` fetches now — the inbox calls it when a message
 * arrives or a conversation is opened, so badges do not wait for the poll.
 * Every change is also broadcast as a `communication:unread-changed` window
 * event, which the bell uses to refetch its own count.
 */

const POLL_MS = 30000;
const EMPTY = { whatsapp: 0, email: 0, chat: 0, total: 0 };

let summary = EMPTY;
let inFlight = null;
let timer = null;
const subscribers = new Set();

const publish = (next) => {
  const changed = ["whatsapp", "email", "chat", "total"].some((key) => next[key] !== summary[key]);
  summary = next;
  subscribers.forEach((notify) => notify(summary));
  if (changed) window.dispatchEvent(new CustomEvent("communication:unread-changed", { detail: summary }));
};

export const refreshUnreadSummary = () => {
  if (!inFlight) {
    inFlight = axios
      .get("/communication/unread-summary/")
      .then((response) => publish({ ...EMPTY, ...(response.data || {}) }))
      .catch(() => {})
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

const onVisible = () => {
  if (document.visibilityState === "visible") refreshUnreadSummary();
};

const start = () => {
  refreshUnreadSummary();
  timer = setInterval(() => {
    if (document.visibilityState === "visible") refreshUnreadSummary();
  }, POLL_MS);
  document.addEventListener("visibilitychange", onVisible);
};

const stop = () => {
  clearInterval(timer);
  timer = null;
  document.removeEventListener("visibilitychange", onVisible);
};

export const useUnreadSummary = () => {
  const [value, setValue] = useState(summary);
  useEffect(() => {
    subscribers.add(setValue);
    if (subscribers.size === 1) start();
    setValue(summary);
    return () => {
      subscribers.delete(setValue);
      if (!subscribers.size) stop();
    };
  }, []);
  return value;
};
